import { ExportMenu } from "@/components/ExportMenu";
import { useEffect, useMemo, useState } from "react";
import api, { money, fmtDate, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import Pagination from "@/components/Pagination";
import { InlineError } from "@/components/InlineError";
import { Plus, Ban, Truck } from "lucide-react";

const CATS = ["Transport / Carburant", "Entretien véhicule", "Péage / Fret", "Loyer", "Salaires", "Fournitures", "Douane", "Marketing", "Électricité / Eau", "Autre"];
const LOGISTIC_CATS = ["Transport / Carburant", "Entretien véhicule", "Péage / Fret"];
const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ category: CATS[0], beneficiary: "", amount: "", method: "cash", note: "", date: today(), vehicle_id: "" });

export default function Expenses() {
  const { current } = useActivity();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidErr, setVoidErr] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState("expenses");
  const [sps, setSps] = useState([]);

  const load = async () => {
    if (!current) return;
    const { data } = await api.get(`/expenses?activity_id=${current.id}`);
    setRows(data); setPage(1);
    try { const v = await api.get("/vehicles"); setVehicles(v.data); } catch (e) { setVehicles([]); }
    try { const sp = await api.get(`/supplier-payments?activity_id=${current.id}`); setSps(sp.data); } catch (e) { setSps([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current]);

  const askSave = () => {
    setSaveErr("");
    if (!(Number(form.amount) > 0)) { setSaveErr("Le montant doit être supérieur à 0."); return; }
    setConfirmSave(true);
  };
  const save = async () => {
    setSaving(true); setSaveErr("");
    try {
      await api.post("/expenses", { activity_id: current.id, ...form, amount: Number(form.amount || 0), account: form.method === "cash" ? "Espèces" : form.method, vehicle_id: form.vehicle_id || null });
      toast.success("Dépense enregistrée"); setConfirmSave(false); setOpen(false); setForm(empty()); load();
    } catch (e) { setConfirmSave(false); setSaveErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };
  const doVoid = async () => {
    setVoidLoading(true); setVoidErr("");
    try { await api.post(`/expenses/${voidTarget.id}/void`); toast.success("Dépense contrepassée"); setVoidTarget(null); load(); }
    catch (e) { setVoidErr(apiErr(e.response?.data?.detail, e)); }
    finally { setVoidLoading(false); }
  };

  const purchaseRows = useMemo(() => sps.map((s) => ({ id: s.id, created_at: s.created_at, category: "Achat fournisseur", beneficiary: s.supplier || s.purchase_number || "Fournisseur", method: s.method, amount: s.amount, voided: false, _purchase: true })), [sps]);
  const listRows = useMemo(() => view === "sorties" ? [...rows, ...purchaseRows].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")) : rows, [view, rows, purchaseRows]);
  const active = useMemo(() => listRows.filter((r) => !r.voided), [listRows]);
  const total = active.reduce((s, r) => s + r.amount, 0);
  const monthKey = today().slice(0, 7);
  const monthTotal = active.filter((r) => (r.created_at || "").slice(0, 7) === monthKey).reduce((s, r) => s + r.amount, 0);
  const byCat = useMemo(() => {
    const m = {};
    active.forEach((r) => { m[r.category] = (m[r.category] || 0) + r.amount; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [active]);
  const vehName = (id) => vehicles.find((v) => v.id === id)?.name;

  if (!current) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Dépenses</h1>
          <p className="text-sm text-[#64748B] mt-1">Contrôlez où part l'argent · {current.name}</p></div>
        <div className="flex flex-wrap gap-2">
          <ExportMenu dataset="expenses" params={{ activity_id: current.id }} />
          <button onClick={() => { setSaveErr(""); setForm(empty()); setOpen(true); }} className="btn-primary" data-testid="new-expense-btn"><Plus className="w-4 h-4" /> Nouvelle dépense</button>
        </div>
      </div>

      <div className="inline-flex rounded-xl bg-[#F1F5F9] p-1">
        <button onClick={() => { setView("expenses"); setPage(1); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === "expenses" ? "bg-white shadow-sm text-[#16324F]" : "text-[#64748B]"}`} data-testid="expenses-tab-expenses">Dépenses</button>
        <button onClick={() => { setView("sorties"); setPage(1); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === "sorties" ? "bg-white shadow-sm text-[#16324F]" : "text-[#64748B]"}`} data-testid="expenses-tab-sorties">Sorties (dépenses + achats)</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4"><div className="text-xs text-[#64748B]">Total (actif)</div><div className="text-2xl font-bold font-head text-[#B91C1C] mt-1">{money(total)}</div></div>
        <div className="card p-4"><div className="text-xs text-[#64748B]">Ce mois-ci</div><div className="text-2xl font-bold font-head text-[#16324F] mt-1">{money(monthTotal)}</div></div>
        <div className="card p-4">
          <div className="text-xs text-[#64748B] mb-2">Répartition par catégorie</div>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {byCat.slice(0, 5).map(([c, v]) => (
              <div key={c} className="flex justify-between text-xs"><span className="text-[#475569]">{c}</span><span className="font-semibold">{money(v)}</span></div>
            ))}
            {byCat.length === 0 && <div className="text-xs text-[#94A3B8]">Aucune dépense</div>}
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full"><thead><tr>
          <th className="th">Date</th><th className="th">Catégorie</th><th className="th">Bénéficiaire</th>
          <th className="th">Mode</th><th className="th text-right">Montant</th><th className="th text-right">Action</th>
        </tr></thead><tbody>
          {listRows.slice((page - 1) * 10, page * 10).map((e) => (
            <tr key={e.id} className={`row ${e.voided ? "opacity-50" : ""}`} data-testid={`expense-row-${e.id}`}>
              <td className="td text-xs">{fmtDate(e.created_at)}</td>
              <td className="td font-medium">{e.category}{e.vehicle_id && vehName(e.vehicle_id) && <span className="ml-1 inline-flex items-center gap-1 text-xs text-[#0E7490]"><Truck className="w-3 h-3" />{vehName(e.vehicle_id)}</span>}</td>
              <td className="td">{e.beneficiary}</td>
              <td className="td">{e.method}</td>
              <td className={`td text-right font-semibold ${e.voided ? "line-through" : "text-[#B91C1C]"}`}>{money(e.amount)}</td>
              <td className="td text-right">{e._purchase ? <span className="text-xs text-[#94A3B8]">Achat · voir Achats</span> : (!e.voided && <button onClick={() => { setVoidErr(""); setVoidTarget(e); }} className="btn-remove !py-1.5 !px-3" data-testid={`void-expense-${e.id}`}><Ban className="w-3.5 h-3.5" /> Contrepasser</button>)}</td>
            </tr>
          ))}
          {listRows.length === 0 && <tr><td colSpan={6} className="td text-center py-10 text-[#94A3B8]">Aucune sortie</td></tr>}
        </tbody></table>
      </div>
      <Pagination page={page} setPage={setPage} total={listRows.length} />

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle dépense" testId="expense-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={askSave} disabled={saving} data-testid="save-expense-btn">Enregistrer</button></>}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Catégorie</label><select className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category-select">{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><label className="lbl">Date</label><input type="date" className="inp" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" /></div>
          </div>
          {LOGISTIC_CATS.includes(form.category) && vehicles.length > 0 && (
            <div><label className="lbl">Véhicule concerné (logistique)</label>
              <select className="inp" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="expense-vehicle-select">
                <option value="">— Aucun —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></div>
          )}
          <div><label className="lbl">Bénéficiaire</label><input className="inp" value={form.beneficiary} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} data-testid="expense-beneficiary-input" placeholder="Ex : Station Total, propriétaire…" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Montant</label><input type="number" className="inp" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
            <div><label className="lbl">Mode</label><select className="inp" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}><option value="cash">Espèces</option><option value="mobile_money">Mobile Money</option><option value="bank">Banque</option></select></div>
          </div>
          <div><label className="lbl">Note</label><input className="inp" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          <InlineError message={saveErr} title="Enregistrement impossible" />
        </div>
      </Modal>

      <ConfirmDialog open={confirmSave} danger={false} title="Confirmer la dépense" message={`Enregistrer une dépense de ${money(Number(form.amount || 0))} (${form.category}) ?`} confirmLabel="Valider et enregistrer" loading={saving} onConfirm={save} onClose={() => setConfirmSave(false)} />
      <ConfirmDialog open={!!voidTarget} title="Contrepasser la dépense" message={`Contrepasser (annuler) cette dépense de ${money(voidTarget?.amount || 0)} ? Un mouvement inverse sera enregistré.`} confirmLabel="Contrepasser" loading={voidLoading} error={voidErr} onConfirm={doVoid} onClose={() => setVoidTarget(null)} />
    </div>
  );
}
